import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Download, FileText, Loader, Calendar, ArrowLeft, Layout, MessageSquare, ChevronDown, ChevronUp, Eye, PlusCircle } from 'react-feather';
import { Navbar } from './Navbar';
import TemplateSelector from './TemplateSelector';
import RunnerGame from './RunnerGame';
import ResumeChat from './ResumeChat';
import RevisedScore from './RevisedScore';
import InterviewPrep from './InterviewPrep';
import HtmlGazeOverlay from './HtmlGazeOverlay';
import { GazeSummary } from './GazeOverlay';
import useGazeKeywords from '../gaze/useGazeKeywords';
import ClassicTemplate from './templates/ClassicTemplate';
import ModernTemplate from './templates/ModernTemplate';
import MinimalTemplate from './templates/MinimalTemplate';
import ExecutiveTemplate from './templates/ExecutiveTemplate';

const api = process.env.REACT_APP_API_URL;

const TEMPLATE_COMPONENTS = {
  modern:    ModernTemplate,
  classic:   ClassicTemplate,
  minimal:   MinimalTemplate,
  executive: ExecutiveTemplate,
};

export default function ATSResume() {
  const navigate = useNavigate();

  const [resume,      setResume]      = useState(null);
  const [dropped,     setDropped]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [downloading, setDownloading] = useState('');
  const [template,    setTemplate]    = useState(() => localStorage.getItem('resumeTemplate') || 'modern');
  const [showTemplates, setShowTemplates] = useState(false);
  const [showChat,    setShowChat]    = useState(false);
  const [lengthMode,  setLengthMode]  = useState('full');      // full | one_page
  const [regenerating, setRegenerating] = useState('');        // '' | 'layout' | 'keywords'
  const [leftover,    setLeftover]    = useState([]);          // keywords the score says are still missing
  const [scoreKey,    setScoreKey]    = useState(0);           // remounts RevisedScore after a regenerate
  const [attempted,   setAttempted]   = useState([]);          // keywords a rebuild already tried, never re-offered
  const [kwResult,    setKwResult]    = useState(null);        // {added, remaining} after a keyword rebuild

  // Recruiter Gaze Preview over the generated resume (full keyword list, paid)
  const [gazeOn, setGazeOn] = useState(false);
  const [gazeStats, setGazeStats] = useState(null);
  const { keywords: gazeKeywords } = useGazeKeywords(gazeOn);
  const handleGazeStats = useCallback(stats => setGazeStats(stats), []);

  // Generate / load resume on mount
  useEffect(() => {
    axios
      .post(`${api}generate-ats-resume/`, {}, { withCredentials: true })
      .then(res => {
        setResume(res.data.resume);
        setDropped(Array.isArray(res.data.dropped_keywords) ? res.data.dropped_keywords : []);
        const steps = JSON.parse(localStorage.getItem('completedSteps') || '[]');
        if (!steps.includes('atsResume')) {
          steps.push('atsResume');
          localStorage.setItem('completedSteps', JSON.stringify(steps));
        }
      })
      .catch(() => toast.error('Failed to generate ATS resume. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  // Regenerate with different options: a new layout, or a mandate to work the
  // leftover keywords in. The backend caches by options, so flipping back to a
  // layout you already generated returns instantly.
  const regenerate = useCallback(async (mode, mustInclude, kind) => {
    setRegenerating(kind);
    try {
      const { data } = await axios.post(`${api}generate-ats-resume/`,
        { length_mode: mode, must_include: mustInclude },
        { withCredentials: true });
      setResume(data.resume);
      setDropped(Array.isArray(data.dropped_keywords) ? data.dropped_keywords : []);
      setLengthMode(mode);
      setLeftover([]);
      setScoreKey(k => k + 1);
      if (kind === 'keywords') {
        // Report exactly what happened, and never re-offer this set: one
        // attempt per keyword, then the honest verdict stands.
        const text = JSON.stringify(data.resume || {}).toLowerCase();
        const added = mustInclude.filter(k => text.includes(String(k).toLowerCase()));
        const remaining = mustInclude.filter(k => !text.includes(String(k).toLowerCase()));
        setAttempted(a => [...a, ...mustInclude]);
        setKwResult({ added, remaining });
        toast.success(added.length
          ? `Worked in ${added.length} of ${mustInclude.length}.`
          : 'These could not be added truthfully. Your resume does not yet support them.');
      } else {
        setKwResult(null);
        toast.success(mode === 'full'
          ? 'Rebuilt at full length with every strong achievement kept.'
          : 'Rebuilt as a tight single page.');
      }
    } catch {
      toast.error('Could not rebuild the resume. Please try again.');
    } finally {
      setRegenerating('');
    }
  }, []);

  // Persist template choice
  const handleTemplateChange = useCallback(id => {
    setTemplate(id);
    localStorage.setItem('resumeTemplate', id);
  }, []);

  // Called by ResumeChat when Claude returns an updated resume
  const handleResumeUpdate = useCallback(updatedResume => {
    setResume(updatedResume);
    toast.success('Resume updated by Claude!');
  }, []);

  const handleDownload = async format => {
    setDownloading(format);
    try {
      const response = await axios.get(
        `${api}download-resume/?file_format=${format}&template=${template}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const contentType = format === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf';
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `ATS_Resume_${template}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(`Failed to download ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading('');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-4">
        <Loader className="animate-spin text-blue-600 w-10 h-10" />
        <p className="text-gray-600 text-lg font-medium">Building your ATS-optimized resume…</p>
        <p className="text-gray-400 text-sm text-center">Writing 3 drafts, refining each, then picking the strongest, usually 1-3 minutes.</p>
        <RunnerGame />
      </div>
    );
  }

  if (!resume) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-gray-600">Something went wrong. Please go back and try again.</p>
        <button onClick={() => navigate('/fullScore')} className="px-6 py-2 bg-blue-600 text-white rounded-lg">
          Go Back
        </button>
      </div>
    );
  }

  const TemplateComponent = TEMPLATE_COMPONENTS[template] || ModernTemplate;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white px-4 py-10">
        <div className="max-w-7xl mx-auto">

          {/* Back link */}
          <button
            onClick={() => navigate('/fullScore')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Score
          </button>

          {/* Page header */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Your ATS-Optimized Resume</h1>
              <p className="text-gray-500 text-sm mt-0.5">Preview · edit with Claude · choose template · download</p>
            </div>
            {/* Layout: the tight single page, or the classic full-length resume */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Layout</span>
              <div className="flex rounded-xl border border-gray-300 overflow-hidden">
                {[['full', 'Full detail'], ['one_page', 'Single page']].map(([mode, label]) => (
                  <button
                    key={mode}
                    disabled={!!regenerating || lengthMode === mode}
                    onClick={() => regenerate(mode, [], 'layout')}
                    className={`px-4 py-2 text-sm font-semibold transition ${
                      lengthMode === mode
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-60'}`}
                  >
                    {regenerating === 'layout' && lengthMode !== mode
                      ? <Loader className="w-4 h-4 animate-spin" />
                      : label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Template selector (collapsible) ── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-4 overflow-hidden">
            <button
              onClick={() => setShowTemplates(v => !v)}
              className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Layout className="w-4 h-4 text-blue-500" />
                Template: <span className="capitalize text-blue-600 ml-1">{template}</span>
              </div>
              {showTemplates ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {showTemplates && (
              <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                <TemplateSelector selected={template} onChange={id => { handleTemplateChange(id); setShowTemplates(false); }} />
              </div>
            )}
          </div>

          {/* ── Main two-column layout ── */}
          <div className="flex flex-col lg:flex-row gap-5">

            {/* ── Left: resume preview ── */}
            <div className="flex-1 min-w-0">
              {/* Download buttons */}
              <div className="flex flex-wrap gap-3 mb-4">
                <button
                  onClick={() => handleDownload('pdf')}
                  disabled={!!downloading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-500 disabled:opacity-50 transition text-sm"
                >
                  {downloading === 'pdf' ? <Loader className="animate-spin w-4 h-4" /> : <Download className="w-4 h-4" />}
                  Download PDF
                </button>
                <button
                  onClick={() => handleDownload('docx')}
                  disabled={!!downloading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-700 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50 transition text-sm"
                >
                  {downloading === 'docx' ? <Loader className="animate-spin w-4 h-4" /> : <FileText className="w-4 h-4" />}
                  Download Word
                </button>
                <button
                  onClick={() => setShowChat(v => !v)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition text-sm ${
                    showChat
                      ? 'bg-violet-600 text-white'
                      : 'bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  {showChat ? 'Hide Chat' : 'Edit with Claude'}
                </button>
                <button
                  onClick={() => setGazeOn(v => !v)}
                  title="Simulate how a recruiter skims this resume in the first pass"
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition text-sm ${
                    gazeOn
                      ? 'bg-orange-500 text-white'
                      : 'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100'
                  }`}
                >
                  <Eye className="w-4 h-4" />
                  Recruiter Gaze
                </button>
              </div>

              {/* Post-generation insights: revised score + interview prep */}
              <div className="flex flex-col gap-4 mb-4">
                <RevisedScore key={scoreKey} onMissing={setLeftover} />
                <InterviewPrep />
              </div>

              {/* The rebuild's verdict: what went in, what honestly could not. */}
              {kwResult && (
                <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-1.5">
                  {kwResult.added.length > 0 && (
                    <p className="text-sm text-emerald-700">
                      <span className="font-semibold">Added to your resume:</span> {kwResult.added.join(', ')}
                    </p>
                  )}
                  {kwResult.remaining.length > 0 && (
                    <p className="text-sm text-amber-800">
                      <span className="font-semibold">Could not be added truthfully:</span> {kwResult.remaining.join(', ')}.
                      <span className="text-gray-500 font-normal"> These need real experience behind them; they are listed in the kept-honest box below.</span>
                    </p>
                  )}
                </div>
              )}

              {/* Leftover keywords: only the genuinely placeable ones are ever
                  offered (dropped ones already live in the kept-honest box),
                  and each keyword gets exactly one attempt. */}
              {(() => {
                const droppedLower = dropped.map(d => String(d).toLowerCase());
                const attemptedLower = attempted.map(a => String(a).toLowerCase());
                const placeable = leftover.filter(k =>
                  !droppedLower.includes(String(k).toLowerCase()) &&
                  !attemptedLower.includes(String(k).toLowerCase()));
                return placeable.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                  <p className="text-sm text-amber-800 font-semibold">
                    {placeable.length} keyword{placeable.length === 1 ? ' is' : 's are'} missing but can be worked in.
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    {placeable.slice(0, 8).join(', ')}{placeable.length > 8 ? ` and ${placeable.length - 8} more` : ''}
                  </p>
                  <button
                    onClick={() => regenerate(lengthMode, placeable, 'keywords')}
                    disabled={!!regenerating}
                    className="mt-3 flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-500 disabled:opacity-50 transition"
                  >
                    {regenerating === 'keywords'
                      ? <><Loader className="w-4 h-4 animate-spin" /> Rebuilding with the missing keywords…</>
                      : <><PlusCircle className="w-4 h-4" /> Work {placeable.length === 1 ? 'it' : `all ${placeable.length}`} into the resume</>}
                  </button>
                  <p className="text-[11px] text-amber-700 mt-2">
                    Each one is placed only where your real experience supports it. Anything that cannot be
                    included truthfully stays out and is listed below, because an invented claim costs you the interview.
                  </p>
                </div>
                );
              })()}

              {/* Honesty notice, keywords we deliberately left out */}
              {dropped.length > 0 && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-semibold text-amber-800 mb-1">
                    ✓ Kept honest, {dropped.length} keyword{dropped.length === 1 ? '' : 's'} left out
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    We didn't add these because your resume doesn't yet support them. Claiming them could backfire in an interview. Add real experience for any you genuinely have, then regenerate.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {dropped.map((k, i) => (
                      <span key={i} className="text-xs bg-white border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full">{k}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Template preview */}
              <div className="bg-white shadow-lg rounded-2xl overflow-auto border border-gray-100">
                <HtmlGazeOverlay enabled={gazeOn} keywords={gazeKeywords} onStats={handleGazeStats}>
                  <TemplateComponent resume={resume} />
                </HtmlGazeOverlay>
              </div>
              {gazeOn && <GazeSummary stats={gazeStats} />}
            </div>

            {/* ── Right: Claude chat ── */}
            {showChat && (
              <div className="w-full lg:w-96 flex-shrink-0">
                <div className="sticky top-24 h-[680px]">
                  <ResumeChat onResumeUpdate={handleResumeUpdate} />
                </div>
              </div>
            )}
          </div>

          {/* ── Book a review CTA ── */}
          <div className="mt-8 bg-orange-50 border border-orange-200 rounded-2xl p-6 text-center">
            <h3 className="text-lg font-bold text-gray-800 mb-1">Want an expert eye on your resume?</h3>
            <p className="text-gray-500 text-sm mb-4">
              Book a 30-minute one-on-one resume review session with one of our specialists.
            </p>
            <button
              onClick={() => navigate('/appointment')}
              className="flex items-center gap-2 mx-auto px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-400 transition"
            >
              <Calendar className="w-5 h-5" />
              Book a Review Session
            </button>
          </div>

        </div>
      </div>
    </>
  );
}
